import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/app/lib/utils';
import Icon from './Icon';
import { CustomScroll } from './CustomScroll';

const Dialog = DialogPrimitive.Root;

const DialogPortal = DialogPrimitive.Portal;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:duration-300 data-[state=open]:duration-300',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export type DrawerProps = React.PropsWithChildren<{
  isOpen?: boolean;
  bodyClassName?: string;
  bodyStyle?: React.CSSProperties;
  wrapperClassName?: string;
  scrollClassName?: string;
  scrollBody?: boolean;
  modal?: boolean;
  showOverlay?: boolean;
  showClose?: boolean;
  closeOnClickOutside?: boolean;
  preventOpenAutoFocus?: boolean;
  title?: string;
  titleIcon?: React.ReactNode;
  subtitle?: React.ReactNode;
  footer?: React.ReactNode;
  subheader?: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  embedded?: boolean;
}>;

export type DrawerRef = {
  open: () => void;
  close: () => void;
};

/** Generic function that prevents any default event behavior. */
const avoidDefaultDomBehavior = (e: Event) => {
  e.preventDefault();
};

const Drawer = React.forwardRef<DrawerRef, DrawerProps>(
  (
    {
      isOpen,
      showClose = true,
      bodyClassName,
      bodyStyle,
      wrapperClassName,
      scrollClassName,
      scrollBody = true,
      modal = true,
      showOverlay = modal,
      closeOnClickOutside,
      preventOpenAutoFocus = true,
      onOpenChange,
      children,
      title,
      titleIcon,
      subtitle,
      subheader,
      footer,
      embedded = false,
    },
    ref
  ) => {
    const [isDrawerOpen, setOpen] = React.useState(false);

    React.useImperativeHandle(ref, () => {
      return {
        open: () => {
          setOpen(true);
        },
        close: () => {
          setOpen(false);
        },
      };
    });

    React.useEffect(() => {
      if (isOpen !== undefined) setOpen(isOpen);
    }, [isOpen]);

    const closeDrawer = () => {
      setOpen(false);
      onOpenChange?.(false);
    };

    if (embedded) {
      return (
        <section
          aria-label={title || 'Contact details'}
          className={cn(
            'flex h-full min-h-0 w-full flex-col overflow-hidden bg-background',
            bodyClassName
          )}
          style={bodyStyle}
        >
          {title ? (
            <div className="border-b border-border p-5">
              <div className="flex items-center gap-1 break-words">
                {titleIcon && <div>{titleIcon}</div>}
                <div>
                  <h1 className="font-semibold">{title}</h1>
                  {subtitle}
                </div>
              </div>
            </div>
          ) : null}
          {subheader && <div className="border-b border-border p-4">{subheader}</div>}
          <div className={cn('flex min-h-0 flex-1 overflow-hidden', wrapperClassName)}>
            {scrollBody ? (
              <CustomScroll orientation="vertical" viewportClassName={cn('p-0', scrollClassName)}>
                {children}
              </CustomScroll>
            ) : (
              children
            )}
          </div>
          {footer && <div className="border-t border-border p-6 pt-4">{footer}</div>}
        </section>
      );
    }

    return (
      <Dialog open={isDrawerOpen} modal={modal} onOpenChange={onOpenChange} aria-hidden={false}>
        <DialogPortal>
          {modal ? <DialogOverlay /> : null}
          {!modal && showOverlay ? (
            <div
              aria-hidden="true"
              data-state={isDrawerOpen ? 'open' : 'closed'}
              className="fixed inset-0 z-50 bg-black/50 dark:bg-muted/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:duration-300 data-[state=open]:duration-300"
              onPointerDown={event => {
                if (!closeOnClickOutside) {
                  event.preventDefault();
                  return;
                }
                closeDrawer();
              }}
            />
          ) : null}
          <DialogPrimitive.Content
            onPointerDownOutside={event => {
              if (!closeOnClickOutside) avoidDefaultDomBehavior(event);
            }}
            onInteractOutside={event => {
              if (!closeOnClickOutside) avoidDefaultDomBehavior(event);
            }}
            onOpenAutoFocus={event => {
              if (preventOpenAutoFocus) event.preventDefault();
            }}
            className={cn(
              'focus:outline-none flex flex-col fixed inset-y-0 right-0 z-50 h-full w-full max-w-2/5 bg-background shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right data-[state=closed]:duration-300 data-[state=open]:duration-300 !overflow-hidden',
              title ? 'p-0' : 'p-6',
              bodyClassName
            )}
            style={bodyStyle}
          >
            {/* Added sr-only title for accessibility */}
            <DialogPrimitive.Title className="sr-only">{title || 'Drawer'}</DialogPrimitive.Title>
            {title ? (
              <div className="border-b border-border p-5 pr-8">
                <div className="flex gap-1 items-center break-words">
                  {titleIcon && <div>{titleIcon}</div>}
                  <div>
                    <DialogPrimitive.Title className="font-semibold">{title}</DialogPrimitive.Title>
                    {subtitle}
                    <DialogPrimitive.Description />
                  </div>
                </div>
              </div>
            ) : null}
            {subheader && <div className="border-b border-border p-4">{subheader}</div>}
            <div className={cn('flex flex-1 overflow-hidden', wrapperClassName)}>
              {scrollBody ? (
                <CustomScroll orientation="vertical" viewportClassName={cn('p-0', scrollClassName)}>
                  {children}
                </CustomScroll>
              ) : (
                children
              )}
            </div>
            {showClose && (
              <DialogPrimitive.Close className="absolute right-[10px] top-[10px] h-8 w-8 flex items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-trasparent hover:bg-secondary data-[state=open]:text-muted-foreground cursor-pointer">
                <Icon name="cross-fill" size={6} />
              </DialogPrimitive.Close>
            )}
            {footer && <div className="border-t border-border pt-4 p-6">{footer}</div>}
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    );
  }
);

Drawer.displayName = 'Drawer';

export default Drawer;
